import { SolarModel } from 'src/Libs/solar/models/solar.model'
import { PvTrackerType, ServerPvInverterIds, SolarModule } from 'src/Elements/SystemSettings/SystemSettingsUtils'
import { AuthFetchWithResponse } from 'src/Types/api'
import { EmsProductType, EmsProductTypeValue } from 'src/Routes/Installation/InstallationModel'
import { fetchFarmServerPvInverters, fetchPvInverterIndexes, fetchPvInverters } from './api/solar.api'

const P20_INSTALLATION_SERIAL_START_DIGITS = '85'

const HIDDEN_SOLAR_SETTINGS_PRODUCT_TYPES: Set<EmsProductTypeValue> = new Set([
  EmsProductType.s20_x_pro,
  EmsProductType.one,
  EmsProductType.xem1200,
  EmsProductType.s10_se,
])

/**
 * Tracker settings don't apply to these product types, so the solar settings should not be shown for them.
 */
export const isSolarSettingsHiddenForProductType = (product: EmsProductTypeValue | undefined): boolean => {
  return !!product && HIDDEN_SOLAR_SETTINGS_PRODUCT_TYPES.has(product)
}

/**
 * TODO: use a existing lodash function here. `lodash.merge` didn't worked out well.
 * @param next
 * @param previous
 */
export const mergePvSettings = (next: SolarModel, previous: SolarModel): SolarModel => {
  return {
    pviAcApparentPower: next.pviAcApparentPower || previous.pviAcApparentPower,
    pviAcCurrent: next.pviAcCurrent || previous.pviAcCurrent,
    pviAcPower: next.pviAcPower || previous.pviAcPower,
    pviAcReactivePower: next.pviAcReactivePower || previous.pviAcReactivePower,
    pviAcVoltage: next.pviAcVoltage || previous.pviAcVoltage,
    pviDcCurrent: next.pviDcCurrent || previous.pviDcCurrent,
    pviDcPower: next.pviDcPower || previous.pviDcPower,
    pviDcVoltage: next.pviDcVoltage || previous.pviDcVoltage,
    pviDeviceState: next.pviDeviceState || previous.pviDeviceState,
    pviEnergyAll: next.pviEnergyAll || previous.pviEnergyAll,
    pviIndex: next.pviIndex || previous.pviIndex,
    pviLastError: next.pviLastError || previous.pviLastError,
    pviRotaryFieldDirection: next.pviRotaryFieldDirection || previous.pviRotaryFieldDirection,
    pviSerialNumber: next.pviSerialNumber || previous.pviSerialNumber,
    pviState: next.pviState || previous.pviState,
    pviTemperature: next.pviTemperature || previous.pviTemperature,
    pviTracker: next.pviTracker || previous.pviTracker,
  }
}

export const determineAllowedTrackersBySerialNumber = (storageSerialNumber: number): PvTrackerType[] => {
  if (storageSerialNumber.toString().startsWith(P20_INSTALLATION_SERIAL_START_DIGITS)) {
    return [PvTrackerType.triple]
  }

  return [PvTrackerType.single, PvTrackerType.double]
}

/**
 * This function returns the index of the currently selected solar module
 * If the installation is a farm the clientStorageId must be provided additionally
 */
export const getCurrentSolarModuleIndex = (solarModules: SolarModule[], pviIndex: string | undefined, storageId?: string): number => {
  return solarModules.findIndex(solarModule => solarModule.storageId === storageId && solarModule.pviIndex.toString() === pviIndex)
}

/**
 * Get all solar modules (pv-inverters) of an installation. In the context of a farm this means combining the solar modules of all clients, if the server is selected.
 *
 * @param storageId
 * @param isFarmMasterSelected
 * @param authFetch
 * @returns
 */
export const getAllSolarModules = async (
  storageId: string | undefined,
  isFarmMasterSelected: boolean,
  authFetch: AuthFetchWithResponse
): Promise<SolarModule[]> => {
  const allIndexes = await fetchPvInverterIndexes(storageId, isFarmMasterSelected, authFetch)

  if (isFarmMasterSelected && allIndexes.value) {
    const solarModulesResponse = await fetchFarmServerPvInverters(allIndexes.value as ServerPvInverterIds, authFetch)

    const solarModules = solarModulesResponse
      .filter(solarModuleResponse => !solarModuleResponse.hasErrors)
      .map(solarModuleResponse => solarModuleResponse.value) as SolarModule[]
    return solarModules
  }

  if (!isFarmMasterSelected && allIndexes) {
    const solarModulesResponse = await fetchPvInverters(storageId, allIndexes.value?.ids ?? [], authFetch)
    const solarModules = solarModulesResponse
      .filter(solarModulesResponse => !solarModulesResponse.hasErrors)
      .map(solarModulesResponse => solarModulesResponse.value) as SolarModule[]
    return solarModules
  }

  return []
}
